import { createApi } from '@reduxjs/toolkit/dist/query/react';
import _ from 'lodash';
import axiosBaseQuery from '../../../axios/axiosBaseQuery';
import { groupsListSchema, GroupStructure } from './GroupAdapter';
import { GroupAPIResponse, GroupCreateAPI } from './group.model';
import GroupsCalls from './GroupCalls';
import { normalize } from 'normalizr';
import Util from '../../../util/Util';
import { IconUpload } from '../groups.model';
import convertPromiseToQueryReturnValue from '../../../axios/convertPromiseToQueryReturnValue';
import { deleteGroup } from './GroupCallTypes';
import { Base64 } from '../../../util/Base64';

export const GroupAPI = createApi({
  reducerPath: 'group',
  // 1 day
  keepUnusedDataFor: 60 * 60 * 24,
  baseQuery: axiosBaseQuery(),
  tagTypes: ['group-data'],
  endpoints(build) {
    return {
      getGroups: build.query({
        query: () => ({
          url: new Util().getGroupServiceURL(),
          method: 'GET',
        }),
        transformResponse: (data: GroupAPIResponse[]) =>
          normalize(data, groupsListSchema) as GroupStructure,
        providesTags: ['group-data'],
      }),

      putGroup: build.mutation({
        queryFn: (props: {
          groupId: string;
          groupData: Partial<GroupCreateAPI>;
          iconUpload?: IconUpload;
        }) => {
          const groupResponse = new GroupsCalls().putGroup(
            props.groupId,
            props.groupData,
            props.iconUpload
          );
          return convertPromiseToQueryReturnValue(groupResponse);
        },
        invalidatesTags: ['group-data'],
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          try {
            // Wait for mutation to succeed
            await queryFulfilled;
            // Pessimistic update the cache
            dispatch(
              GroupAPI.util.updateQueryData('getGroups', undefined, (draft) => {
                const group = draft.entities.groups?.[arg.groupId];
                if (group) {
                  // Update the group with the new data
                  Object.assign(group, arg.groupData);
                }
              })
            );
          } catch (err) {
            /* empty */
          }
        },
      }),

      deleteDeviceGroup: build.mutation({
        query: (args: { groupId: string; commanderId: string }) => {
          const { groupId, commanderId } = args;
          return {
            url: `${new Util().getGroupServiceURL()}/${Base64.encode(
              groupId
            )}/devices/${Base64.encode(commanderId)}`,
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            },
          };
        },
        invalidatesTags: ['group-data'],
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          try {
            // Wait for mutation to succeed
            await queryFulfilled;
            // Pessimistic update the cache
            dispatch(
              GroupAPI.util.updateQueryData('getGroups', undefined, (draft) => {
                const group = draft.entities.groups?.[arg.groupId];
                if (group) {
                  group.devices = group.devices.filter(
                    (commanderId) => commanderId !== arg.commanderId
                  );
                }
              })
            );
          } catch (err) {
            /* empty */
          }
        },
      }),
    };
  },
});
